import { useState, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCatalogSkuSearch } from '../../hooks/useWmsSkus';
import type { AddReceivingLineInput, SkuSearchResult } from '../../types';

interface AddReceivingLineModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddReceivingLineInput) => Promise<void>;
  isLoading?: boolean;
}

export function AddReceivingLineModal({
  open,
  onClose,
  onSubmit,
  isLoading,
}: AddReceivingLineModalProps) {
  const [skuPopoverOpen, setSkuPopoverOpen] = useState(false);
  const [skuSearchQuery, setSkuSearchQuery] = useState('');
  const [selectedSku, setSelectedSku] = useState<SkuSearchResult | null>(null);
  const [description, setDescription] = useState('');
  const [expectedQuantity, setExpectedQuantity] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('');
  const [notes, setNotes] = useState('');

  const { data: skuResults, isLoading: skusLoading } = useCatalogSkuSearch(
    skuSearchQuery || undefined,
    50,
    skuPopoverOpen,
  );

  const filteredSkus = useMemo(() => {
    if (!skuResults) return [];
    if (!skuSearchQuery) return skuResults;
    const q = skuSearchQuery.toLowerCase();
    return skuResults.filter(
      (s) =>
        s.sku.toLowerCase().includes(q) ||
        s.nameEn?.toLowerCase().includes(q) ||
        s.nameFr?.toLowerCase().includes(q),
    );
  }, [skuResults, skuSearchQuery]);

  useEffect(() => {
    if (open) {
      setSkuPopoverOpen(false);
      setSkuSearchQuery('');
      setSelectedSku(null);
      setDescription('');
      setExpectedQuantity('');
      setUnitOfMeasure('');
      setNotes('');
    }
  }, [open]);

  const handleSelectSku = (sku: SkuSearchResult) => {
    setSelectedSku(sku);
    setDescription(sku.nameEn || sku.nameFr || '');
    setSkuPopoverOpen(false);
    setSkuSearchQuery('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseInt(expectedQuantity, 10);
    if (isNaN(qty) || qty <= 0) return;

    try {
      await onSubmit({
        sku_code: selectedSku?.sku || undefined,
        sku_name: description || undefined,
        expected_quantity: qty,
        unit_of_measure: unitOfMeasure || undefined,
        notes: notes || undefined,
      });
      toast.success('Receiving line added successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to add receiving line: ${message}`);
      console.error('Error adding receiving line:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Receiving Line</DialogTitle>
          <DialogDescription>
            Select a SKU from the catalog to add a receiving line.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="sku">SKU *</Label>
                <Popover open={skuPopoverOpen} onOpenChange={setSkuPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={skuPopoverOpen}
                      className={cn(
                        'w-full justify-between font-normal',
                        !selectedSku && 'text-muted-foreground',
                      )}
                    >
                      <span className="truncate">
                        {selectedSku ? selectedSku.sku : 'Select SKU...'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search by SKU, name, or manufacturer..."
                        value={skuSearchQuery}
                        onValueChange={setSkuSearchQuery}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {skusLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading SKUs...
                            </span>
                          ) : (
                            'No SKU found.'
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredSkus.map((sku) => (
                            <CommandItem
                              key={sku.id}
                              value={sku.id}
                              onSelect={() => handleSelectSku(sku)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedSku?.id === sku.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="font-mono font-medium text-sm">{sku.sku}</span>
                                <span className="text-xs text-muted-foreground truncate">
                                  {sku.nameEn || sku.nameFr}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedSku && (
                  <p className="text-xs text-muted-foreground">
                    {selectedSku.manufacturerName ? `${selectedSku.manufacturerName} · ` : ''}
                    {selectedSku.nameEn || selectedSku.nameFr}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="uom">Unit of Measure</Label>
                <Input
                  id="uom"
                  value={unitOfMeasure}
                  onChange={(e) => setUnitOfMeasure(e.target.value)}
                  placeholder="e.g., EA, BOX"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Item description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expected_quantity">Expected Quantity *</Label>
              <Input
                id="expected_quantity"
                type="number"
                min="1"
                value={expectedQuantity}
                onChange={(e) => setExpectedQuantity(e.target.value)}
                placeholder="Enter expected quantity"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this line"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !expectedQuantity || !selectedSku}>
              {isLoading ? 'Adding...' : 'Add Line'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
